/**
 * Schema-against-itself consistency checks.
 *
 * `validate.ts` checks data against a schema. This module checks a SCHEMA
 * against its own internal coherence — the kind of soft invariants that
 * aren't enforced by JSON Schema's metamodel but that the Bliksys engine
 * (and any sane validator) needs in practice.
 *
 * The schema editor calls this on every change so the developer sees
 * inconsistencies before saving. None of these are fatal — the engine
 * still renders the schema — but they almost always indicate a mistake.
 */

import type { JsonSchema } from '@bliksys/shared';

import { resolveSchema } from './resolver.js';

export interface SchemaIssue {
  /** JSON pointer into the SCHEMA where the issue was found. */
  path: string;
  message: string;
  severity: 'warning' | 'error';
}

export function validateSchemaConsistency(schema: JsonSchema): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  walk(schema, schema, '', issues);
  return issues;
}

function walk(
  schema: JsonSchema,
  rootSchema: JsonSchema,
  path: string,
  issues: SchemaIssue[],
): void {
  // `required` entries must each name a property that exists in
  // `properties`. We only emit the warning at the immediate site of the
  // mismatch — going deeper would generate noise as the schema grows.
  if (Array.isArray(schema.required) && schema.properties) {
    const knownProps = new Set(Object.keys(schema.properties));
    for (const name of schema.required) {
      if (typeof name !== 'string') continue;
      if (!knownProps.has(name)) {
        issues.push({
          path: `${path}/required`,
          message: `Required field "${name}" doesn't appear in properties.`,
          severity: 'warning',
        });
      }
    }
  }

  // `discriminator.propertyName` must exist (be pinned to a constant) in
  // every variant of the corresponding oneOf / anyOf. If it doesn't,
  // Bliksys falls back to inferring a discriminator, which may pick a
  // different field than intended.
  if (schema.discriminator?.propertyName) {
    const variants = schema.oneOf ?? schema.anyOf;
    if (variants && variants.length > 0) {
      const propName = schema.discriminator.propertyName;
      const resolvedVariants = variants.map((v) => resolveSchema(v, rootSchema));
      const unpinnedAt: number[] = [];
      for (let i = 0; i < resolvedVariants.length; i++) {
        const resolved = resolvedVariants[i];
        if (!resolved) continue;
        const prop = resolved.properties?.[propName];
        const isPinned =
          prop !== undefined &&
          (prop.const !== undefined || (Array.isArray(prop.enum) && prop.enum.length === 1));
        if (!isPinned) unpinnedAt.push(i);
      }
      if (unpinnedAt.length > 0) {
        // Suggest a better propertyName: any property pinned to a constant
        // across all variants.
        const candidate = findCommonConstProperty(resolvedVariants);
        const hint = candidate
          ? ` Did you mean "${candidate}"?`
          : ' No common pinned property found across variants — consider adding one.';
        for (const i of unpinnedAt) {
          issues.push({
            path: `${path}/oneOf/${i}`,
            message: `Variant doesn't pin "${propName}" to a constant; the discriminator won't match.${hint}`,
            severity: 'warning',
          });
        }
      }
    }
  }

  // `default` should be valid against the schema. We do a structural
  // check (correct type, in enum if enum present) — full validation is
  // overkill here.
  if (schema.default !== undefined) {
    const t = inferType(schema);
    if (t !== undefined && jsTypeOf(schema.default) !== t) {
      // A number-default for a string field, etc.
      issues.push({
        path: `${path}/default`,
        message: `Default value type doesn't match the field type (${String(t)}).`,
        severity: 'warning',
      });
    }
    if (Array.isArray(schema.enum) && !schema.enum.some((e) => jsonEqual(e, schema.default))) {
      issues.push({
        path: `${path}/default`,
        message: `Default value isn't in the allowed enum.`,
        severity: 'warning',
      });
    }
  }

  // Recurse.
  if (schema.properties) {
    for (const [key, sub] of Object.entries(schema.properties)) {
      walk(sub, rootSchema, `${path}/properties/${escapePointerSegment(key)}`, issues);
    }
  }
  if (schema.items) {
    walk(schema.items, rootSchema, `${path}/items`, issues);
  }
  for (const k of ['oneOf', 'anyOf'] as const) {
    const variants = schema[k];
    if (variants) {
      variants.forEach((v, i) => walk(v, rootSchema, `${path}/${k}/${i}`, issues));
    }
  }
  if (schema.$defs) {
    for (const [key, sub] of Object.entries(schema.$defs)) {
      walk(sub, rootSchema, `${path}/$defs/${escapePointerSegment(key)}`, issues);
    }
  }
}

function inferType(schema: JsonSchema): JsonSchema['type'] | undefined {
  if (schema.type !== undefined) {
    return Array.isArray(schema.type) ? schema.type.find((t) => t !== 'null') : schema.type;
  }
  if (schema.properties) return 'object';
  if (schema.items) return 'array';
  return undefined;
}

function jsTypeOf(v: unknown): JsonSchema['type'] | undefined {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'object') return 'object';
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number') return Number.isInteger(v) ? 'integer' : 'number';
  if (typeof v === 'string') return 'string';
  return undefined;
}

function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function escapePointerSegment(s: string): string {
  return s.replace(/~/g, '~0').replace(/\//g, '~1');
}

/**
 * Find a property name that every variant pins to a constant. Returns
 * undefined when no such property exists. Used to suggest a discriminator
 * propertyName when the configured one doesn't match.
 */
function findCommonConstProperty(variants: readonly JsonSchema[]): string | undefined {
  if (variants.length === 0) return undefined;
  const firstKeys = Object.keys(variants[0]?.properties ?? {});
  for (const key of firstKeys) {
    const allPin = variants.every((v) => {
      const prop = v.properties?.[key];
      if (!prop) return false;
      if (prop.const !== undefined) return true;
      if (Array.isArray(prop.enum) && prop.enum.length === 1) return true;
      return false;
    });
    if (allPin) return key;
  }
  return undefined;
}
